import { ReactNode } from 'react';
import { Box, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { PersonnelPicker } from '../PersonnelPicker/PersonnelPicker';

const useStyles = makeStyles(theme => ({
  grid: { marginTop: theme.spacing(0.5) },
}));

export interface FourInTheBoxValues {
  lead?: string;
  pm?: string;
  techLead?: string;
  architect?: string;
}

export interface FourInTheBoxSectionProps {
  value: FourInTheBoxValues;
  onChange: (values: FourInTheBoxValues) => void;
  /**
   * Fired only when a new person is assigned into any leadership slot (not
   * on clear) — mirrors MembersPicker's onMemberAdded (PAT-779), so a parent
   * can react to a leadership assignment the same way it reacts to a
   * regular member being added.
   */
  onMemberAdded?: (ref: string) => void;
  /** Fired only when a leadership slot is cleared — the mirror of onMemberAdded. */
  onMemberRemoved?: (ref: string) => void;
  /**
   * Optional per-slot action rendered next to the assigned person (e.g. a
   * primary-team star toggle) — mirrors MembersPicker's renderMemberAction,
   * so a parent (TeamEditForm) can offer the same control uniformly whether
   * someone is a plain member or holds a leadership role.
   */
  renderMemberAction?: (ref: string) => ReactNode;
}

const ROLES: { key: keyof FourInTheBoxValues; label: string }[] = [
  { key: 'lead', label: 'Engineering Manager (EM)' },
  { key: 'pm', label: 'Product Manager (PM)' },
  { key: 'techLead', label: 'Tech Lead' },
  { key: 'architect', label: 'Architect' },
];

export function FourInTheBoxSection({
  value,
  onChange,
  onMemberAdded,
  onMemberRemoved,
  renderMemberAction,
}: FourInTheBoxSectionProps) {
  const classes = useStyles();
  // Collect all currently-assigned refs so PersonnelPicker can disable
  // already-assigned users in other slots (FR-028: max one user per role)
  const assignedRefs = Object.values(value).filter(
    (v): v is string => typeof v === 'string',
  );

  const handleChange =
    (key: keyof FourInTheBoxValues) => (ref: string | undefined) => {
      onChange({ ...value, [key]: ref });
    };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Team Leadership
      </Typography>
      <Typography variant="caption" color="textSecondary" gutterBottom>
        At least one of Engineering Manager or Product Manager is required — a team
        can't be ownerless. Tech Lead and Architect are optional. Each role may be
        held by one person only.
      </Typography>
      <Grid container spacing={2} className={classes.grid}>
        {ROLES.map(({ key, label }) => (
          <Grid item xs={12} sm={6} key={key}>
            <PersonnelPicker
              label={label}
              value={value[key]}
              onChange={handleChange(key)}
              // Disable refs already used in OTHER slots (not this one)
              disabledRefs={assignedRefs.filter(r => r !== value[key])}
              onMemberAdded={onMemberAdded}
              onMemberRemoved={onMemberRemoved}
              renderMemberAction={renderMemberAction}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
